import React, { useState, useEffect } from 'react';
import { Modal, Button, Row, Col, Card, Badge, Spinner, Alert } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';

const ApplicantProfile = ({ show, onHide, atsNumber }) => {
  const [applicant, setApplicant] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (show && atsNumber) {
      fetchApplicant();
    }
  }, [show, atsNumber]);

  const fetchApplicant = async () => {
    try {
      setLoading(true);
      setError(null);
    const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  const response = await axios.get(`${base}/api/applicants/${atsNumber}`, { withCredentials: true });
      setApplicant(response.data);
    } catch (error) {
      console.error('Error fetching applicant:', error);
      setError('Failed to load applicant data');
      toast.error('Failed to load applicant data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (applicant) => {
    if (applicant.rejected) return <Badge bg="danger">Rejected</Badge>;
    if (applicant.left) return <Badge bg="warning" text="dark">Left</Badge>;
    if (applicant.assigned_project) return <Badge bg="success">Assigned</Badge>;
    if (applicant.registered_startupworld) return <Badge bg="info">Registered</Badge>;
    if (applicant.attended_meet_greet) return <Badge bg="primary">Attended</Badge>;
    return <Badge bg="secondary">New</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatPhone = (countryCode, phone) => {
    if (!phone) return 'Not provided';
    return `${countryCode || ''} ${phone}`.trim();
  };

  if (!show) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Applicant Profile</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2">Loading applicant data...</p>
          </div>
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : applicant ? (
          <div className="applicant-profile">
            {/* Header Section */}
            <div className="profile-header mb-4">
              <div className="ats-number-display">
                <h2 className="ats-number">{applicant.ats_number}</h2>
                <div className="status-badge">
                  {getStatusBadge(applicant)}
                </div>
              </div>
              <h3 className="applicant-name">
                {applicant.first_name} {applicant.last_name}
              </h3>
            </div>

            {/* Personal Information */}
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Personal Information</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <div className="info-item">
                      <label>Email Address</label>
                      <p>{applicant.email_id || 'Not provided'}</p>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="info-item">
                      <label>Phone Number</label>
                      <p>{formatPhone(applicant.country_code, applicant.phone_number)}</p>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <div className="info-item">
                      <label>Domain</label>
                      <p>{applicant.domain || 'Not specified'}</p>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="info-item">
                      <label>Source</label>
                      <p>{applicant.source || 'Unknown'}</p>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <div className="info-item">
                      <label>Location</label>
                      <p>{applicant.location || 'Not specified'}</p>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="info-item">
                      <label>Slot Number</label>
                      <p>{applicant.slot_no || 'Not assigned'}</p>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Application Details */}
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Application Details</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <div className="info-item">
                      <label>Application Date</label>
                      <p>{formatDate(applicant.application_date)}</p>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="info-item">
                      <label>Meet & Greet Date</label>
                      <p>{formatDate(applicant.meet_greet_date)}</p>
                    </div>
                  </Col>
                </Row>
                {applicant.resume_link && (
                  <Row>
                    <Col md={12}>
                      <div className="info-item">
                        <label>Resume Link</label>
                        <p>
                          <a href={applicant.resume_link} target="_blank" rel="noopener noreferrer">
                            View Resume
                          </a>
                        </p>
                      </div>
                    </Col>
                  </Row>
                )}
              </Card.Body>
            </Card>

            {/* Status Information */}
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Status Information</h5>
              </Card.Header>
              <Card.Body>
                <Row className="status-row">
                  <Col md={6}>
                    <div className="status-item">
                      <label>Meet & Greet Attendance</label>
                      <div className="status-indicator">
                        {applicant.attended_meet_greet ? (
                          <Badge bg="success">Attended</Badge>
                        ) : (
                          <Badge bg="secondary">Not Attended</Badge>
                        )}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="status-item">
                      <label>StartupWorld Registration</label>
                      <div className="status-indicator">
                        {applicant.registered_startupworld ? (
                          <Badge bg="info">Registered</Badge>
                        ) : (
                          <Badge bg="secondary">Not Registered</Badge>
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>
                <Row className="status-row">
                  <Col md={6}>
                    <div className="status-item">
                      <label>Project Assignment</label>
                      <div className="status-indicator">
                        {applicant.assigned_project ? (
                          <Badge bg="success">Assigned</Badge>
                        ) : (
                          <Badge bg="secondary">Not Assigned</Badge>
                        )}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="status-item">
                      <label>Program Status</label>
                      <div className="status-indicator">
                        {applicant.rejected ? (
                          <Badge bg="danger">Rejected</Badge>
                        ) : applicant.left ? (
                          <Badge bg="warning" text="dark">Left Program</Badge>
                        ) : (
                          <Badge bg="success">Active</Badge>
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Timestamps */}
            <Card>
              <Card.Header>
                <h5 className="mb-0">System Information</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <div className="info-item">
                      <label>Created</label>
                      <p>{formatDate(applicant.created_at)}</p>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="info-item">
                      <label>Last Updated</label>
                      <p>{formatDate(applicant.updated_at)}</p>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>

      <style jsx>{`
        .applicant-profile {
          max-height: 70vh;
          overflow-y: auto;
        }

        .profile-header {
          text-align: center;
          padding: 20px 0;
          border-bottom: 1px solid #dee2e6;
        }

        .ats-number-display {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;
          margin-bottom: 12px;
        }

        .ats-number {
          font-family: 'Courier New', monospace;
          font-size: 24px;
          font-weight: 700;
          color: #007bff;
          margin: 0;
          background: #e3f2fd;
          padding: 8px 16px;
          border-radius: 6px;
        }

        .applicant-name {
          font-size: 28px;
          font-weight: 600;
          color: #2c3e50;
          margin: 0;
        }

        .info-item, .status-item {
          margin-bottom: 8px;
        }

        .info-item label, .status-item label {
          font-weight: 600;
          color: #495057;
          font-size: 14px;
          margin-bottom: 0;
          display: block;
        }

        .info-item p {
          margin: 0;
          color: #2c3e50;
          font-size: 16px;
          line-height: 1.25;
        }

        .status-indicator {
          margin-top: 0;
        }

        /* Align status rows: label on left, badge on right in one line */
        .status-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          width: 100%;
        }
        .status-item label {
          margin: 0;
          flex: 1;
        }
        .status-item .status-indicator {
          flex-shrink: 0;
        }

        /* Normalize grid spacing for consistent alignment across columns */
        .applicant-profile .card-body .row {
          row-gap: 6px;
        }
        .applicant-profile .card-body .row > [class*='col-'] {
          display: flex;
        }
        .applicant-profile .card-body .row > [class*='col-'] > .info-item,
        .applicant-profile .card-body .row > [class*='col-'] > .status-item {
          width: 100%;
        }

        /* Add horizontal space between the two status columns to avoid visual overlap */
        .status-row {
          gap: 24px;
        }

        .card-header {
          background-color: #f8f9fa;
          border-bottom: 1px solid #dee2e6;
        }

        .card-header h5 {
          color: #495057;
          font-weight: 600;
        }

        .badge {
          font-size: 12px;
          padding: 6px 12px;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .ats-number-display {
            flex-direction: column;
            gap: 8px;
          }

          .ats-number {
            font-size: 20px;
          }

          .applicant-name {
            font-size: 24px;
          }
        }
      `}</style>
    </Modal>
  );
};

export default ApplicantProfile;
